import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Copy, Download, Home, Check } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConfirmationPaiement = () => {
  const { devisId } = useParams();
  const navigate = useNavigate();
  const [devis, setDevis] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchDevis();
  }, [devisId]);

  const fetchDevis = async () => {
    try {
      const response = await axios.get(`${API}/devis/${devisId}`);
      setDevis(response.data);
    } catch (err) {
      console.error('Error fetching devis:', err);
    }
  };

  const calculatePrice = (volume, type) => {
    const prices = {
      "3m³": { "Gravats": "245", "Gravat/terre": "245", "Terre": "245" },
      "8m³": { "Gravats": "300", "Mélange (DIB)": "400" },
      "10m³": { "Gravats": "350", "Mélange (DIB)": "480" },
      "15m³": { "Gravats": null, "Mélange (DIB)": "675" },
      "20m³": { "Gravats": null, "Mélange (DIB)": "860" },
      "30m³": { "Gravats": null, "Mélange (DIB)": "1185" }
    };
    
    if (volume in prices && type in prices[volume]) {
      return prices[volume][type];
    }
    return "0";
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!devis) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Chargement...</p>
      </div>
    );
  }

  const price = calculatePrice(devis.benne_volume, devis.benne_type);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Devis Accepté !</h1>
          <p className="text-gray-600">Merci pour votre confiance. Voici les instructions de paiement.</p>
        </div>

        {/* Payment Instructions */}
        <Card className="p-8 mb-6 bg-gradient-to-br from-green-50 to-white border-2 border-green-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            💳 Paiement par Virement Bancaire
          </h2>

          <div className="space-y-6">
            {/* Montant */}
            <div className="bg-white p-6 rounded-lg border-2 border-green-500">
              <p className="text-sm text-gray-600 mb-1">Montant à virer</p>
              <p className="text-4xl font-bold text-green-600">{price}€ HT</p>
              <p className="text-sm text-gray-500 mt-2">Réf: DEVIS-{devisId}</p>
            </div>

            {/* Coordonnées bancaires */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="font-bold text-lg mb-4 text-gray-900">Coordonnées Bancaires</h3>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Titulaire du compte</p>
                  <div className="flex items-center justify-between bg-gray-50 p-3 rounded">
                    <p className="font-semibold">Location Benne Chantier</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">IBAN</p>
                  <div className="flex items-center justify-between bg-gray-50 p-3 rounded">
                    <p className="font-mono font-semibold text-sm">FR76 1695 8000 0134 1500 7564 781</p>
                    <button
                      onClick={() => copyToClipboard('FR76169580000134150075647812')}
                      className="ml-2 p-2 hover:bg-gray-200 rounded transition-colors"
                      title="Copier l'IBAN"
                    >
                      {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-gray-600" />}
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">BIC/SWIFT</p>
                  <div className="flex items-center justify-between bg-gray-50 p-3 rounded">
                    <p className="font-mono font-semibold">QNTOFRP1XXX</p>
                    <button
                      onClick={() => copyToClipboard('QNTOFRP1XXX')}
                      className="ml-2 p-2 hover:bg-gray-200 rounded transition-colors"
                      title="Copier le BIC"
                    >
                      {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-gray-600" />}
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Banque</p>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="font-semibold">Qonto SA</p>
                    <p className="text-sm text-gray-600">18 rue de Navarin, 75009 Paris, France</p>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-yellow-900 mb-2">⚠️ Important - Motif du virement</p>
                  <div className="flex items-center justify-between bg-white p-3 rounded">
                    <p className="font-mono text-sm">DEVIS-{devisId}</p>
                    <button
                      onClick={() => copyToClipboard(`DEVIS-${devisId}`)}
                      className="ml-2 p-2 hover:bg-gray-100 rounded transition-colors"
                      title="Copier la référence"
                    >
                      {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-gray-600" />}
                    </button>
                  </div>
                  <p className="text-xs text-yellow-800 mt-2">
                    N'oubliez pas d'indiquer cette référence pour que nous puissions identifier votre paiement
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Next Steps */}
        <Card className="p-6 mb-6">
          <h3 className="font-bold text-lg mb-4 text-gray-900">📋 Prochaines étapes</h3>
          <ol className="space-y-3">
            <li className="flex items-start">
              <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
              <div>
                <p className="font-semibold">Effectuez le virement</p>
                <p className="text-sm text-gray-600">Utilisez les coordonnées bancaires ci-dessus</p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
              <div>
                <p className="font-semibold">Confirmation de réception</p>
                <p className="text-sm text-gray-600">Nous vous confirmons la réception du paiement sous 24-48h</p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
              <div>
                <p className="font-semibold">Livraison de votre benne</p>
                <p className="text-sm text-gray-600">Livraison dans les 24h après confirmation du paiement</p>
              </div>
            </li>
          </ol>
        </Card>

        {/* Contact */}
        <Card className="p-6 bg-gray-50">
          <h3 className="font-bold text-lg mb-4 text-gray-900">📞 Besoin d'aide ?</h3>
          <p className="text-gray-700 mb-4">Notre équipe est à votre disposition pour toute question</p>
          <div className="space-y-2">
            <p className="text-sm">
              <strong>Téléphone :</strong> <a href="tel:0745558835" className="text-green-600 hover:text-green-700">07 45 55 88 35</a>
            </p>
            <p className="text-sm">
              <strong>Email :</strong> <a href="mailto:contact.mna.locationbennes@gmail.com" className="text-green-600 hover:text-green-700">contact.mna.locationbennes@gmail.com</a>
            </p>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex justify-center gap-4 mt-8">
          <Button
            onClick={() => window.print()}
            variant="outline"
            className="flex items-center"
          >
            <Download className="mr-2 h-4 w-4" />
            Imprimer
          </Button>
          <Button
            onClick={() => navigate('/')}
            className="bg-green-600 hover:bg-green-700 text-white flex items-center"
          >
            <Home className="mr-2 h-4 w-4" />
            Retour à l'accueil
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationPaiement;
